/**
 * This module is for selection of session specific kernel draft information.
 */
import { createSelector } from "reselect";
import { KernelTelemetryData } from "../../telemetry/kernelTelemetry";
import { Language, LanguageId, LanguageName } from "../../types/language";
import { SourceType } from "../../types/SourceType";
import {
  getCurrentAccelerator,
  getCurrentInternetEnabled,
  getCurrentKernelId,
  getCurrentKernelRunId,
  getCurrentLanguageId,
  getCurrentLegacyStatusId,
} from "./current";
import { getKernelDrafts } from "./entities";
import { getJupyterProxyUrl } from "./jupyter";
import { getSourceType } from "./session";
import { getEditorErrorMessage } from "./status";
import { getThemeName } from "./theme";

/** notebook/python langauge. */
export const LANGUAGE_NOTEBOOK_PYTHON: Language = {
  id: 8,
  sourceType: "notebook",
  name: "python",
};

/** notebook/r langauge. */
export const LANGUAGE_NOTEBOOK_R: Language = {
  id: 13,
  sourceType: "notebook",
  name: "r",
};

/** script/python langauge. */
export const LANGUAGE_SCRIPT_PYTHON: Language = {
  id: 14,
  sourceType: "script",
  name: "python",
};

/** script/r langauge. */
export const LANGUAGE_SCRIPT_R: Language = {
  id: 15,
  sourceType: "script",
  name: "r",
};

/** script/rmarkdown langauge. */
export const LANGUAGE_SCRIPT_RMARKDOWN: Language = {
  id: 16,
  sourceType: "script",
  name: "rmarkdown",
};

/** Statically sets all supported languages. */
export const LANGUAGES: Language[] = [
  LANGUAGE_NOTEBOOK_PYTHON,
  LANGUAGE_NOTEBOOK_R,
  LANGUAGE_SCRIPT_R,
  LANGUAGE_SCRIPT_PYTHON,
  LANGUAGE_SCRIPT_RMARKDOWN,
];

/** Dedupes static supported language names. */
const SUPPORTED_LANGUAGE_NAMES = LANGUAGES.reduce((languageNames, language) => {
  if (!languageNames.includes(language.name)) {
    languageNames.push(language.name);
  }
  return languageNames;
}, [] as LanguageName[]);

/** Statically gets the specific language per an id. */
export const getLanguageFromId = (languageId: LanguageId) => {
  const language = LANGUAGES.find(({ id }) => id === languageId);
  if (!language) {
    throw new Error(`languageId ${languageId} is unsupported.`);
  }
  return language;
};

/** Statically gets all languages for a given source type. */
export const getLanguagesBySourceType = (sourceType: SourceType) =>
  LANGUAGES.filter(language => language.sourceType === sourceType);

/** Ensure that a languageName is supported and return a type-safe version. */
export const ensureSupportedLanguageName = (
  languageName: string
): LanguageName => {
  const maybeLanguageName = languageName as LanguageName;
  if (SUPPORTED_LANGUAGE_NAMES.includes(maybeLanguageName)) {
    return maybeLanguageName;
  }
  throw new Error(`Unsupported languageName: ${languageName}`);
};

/** Statically gets a language by sourceType and languageName. */
export const getLanguageBySourceTypeAndName = (
  sourceType: SourceType,
  languageName: LanguageName
) =>
  getLanguagesBySourceType(sourceType).find(
    language => language.name === languageName
  ) || null;

/** Selects the current kernel draft. */
export const getCurrentKernelDraft = createSelector(
  [getKernelDrafts, getCurrentKernelId],
  (kernelDrafts, kernelId) => {
    if (kernelDrafts && kernelId) {
      return kernelDrafts[kernelId] || null;
    }
    return null;
  }
);

/** Selects the blob of the current kernel draft. */
const getCurrentKernelDraftBlob = createSelector(
  [getCurrentKernelDraft],
  draft => (draft && draft.blob) || null
);

/** Selects kernel settings of kernel draft blob settings. */
const getCurrentKernelDraftBlobSettings = createSelector(
  [getCurrentKernelDraftBlob],
  blob => (blob && blob.settings) || null
);

/** Selects the parent kernel version id off the kernel draft. */
export const getParentKernelVersionId = createSelector(
  [getCurrentKernelDraft],
  draft => {
    return draft ? draft.parentKernelVersionId : null;
  }
);

/** Selects the language name of kernel draft blob settings. */
export const getKernelDraftLanguageName = createSelector(
  [getCurrentKernelDraftBlobSettings],
  settings => (settings && settings.language) || null
);

/** Gets the current language based on session id. */
export const getCurrentLanguage = createSelector(
  [getCurrentLanguageId],
  languageId => (languageId ? getLanguageFromId(languageId) : null)
);

/** Gets the current Jupyter kernel name. */
export const getCurrentJupyterKernelName = createSelector(
  [getCurrentLanguageId],
  languageId => {
    switch (languageId) {
      case 14:
      case 8:
        return "python3";
      case 13:
      case 15:
      case 16:
        return "ir";
      default:
        return null;
    }
  }
);

/** Gets the current language name based on session id. */
export const getCurrentLanguageName = createSelector(
  [getCurrentLanguage],
  language => (language ? language.name : null)
);

/** Gets all current supported languages for the draft source type. */
export const getCurrentSourceTypeLanguages = createSelector(
  [getSourceType],
  sourceType => getLanguagesBySourceType(sourceType)
);

export const formatLanguageDisplayName = (languageName: LanguageName) => {
  switch (languageName) {
    case "python":
      return "Python";
    case "r":
      return "R";
    case "rmarkdown":
      return "RMarkdown";
    default:
      return "UNKNOWN";
  }
};

/** Selects the language dropdown options for the current source type. */
export const getCurrentSourceTypeLanguageOptions = createSelector(
  [getCurrentSourceTypeLanguages],
  languages =>
    languages.map(language => ({
      key: language.id,
      value: formatLanguageDisplayName(language.name),
    }))
);

/** Selects the current language of kernel draft blob settings. */
export const getKernelDraftLanguage = createSelector(
  [getSourceType, getKernelDraftLanguageName],
  (sourceType, languageName) => {
    if (languageName === null) {
      return null;
    }
    if (languageName === "julia" || languageName === "sqlite") {
      throw new Error(`Unsupported languageName: ${languageName}`);
    }
    const languages = getLanguagesBySourceType(sourceType);
    const language = languages.find(({ name }) => name === languageName);
    if (!language) {
      throw new Error(`${sourceType}/${languageName} is not a valid language.`);
    }
    return language;
  }
);

/** Selects the current language id of kernel draft blob settings. */
export const getKernelDraftLanguageId = createSelector(
  [getKernelDraftLanguage],
  language => (language ? language.id : null)
);

/** Selects current telemetry data. */
export const getCurrentKernelTelemetryData = createSelector(
  [
    getSourceType,
    getCurrentLegacyStatusId,
    getCurrentKernelRunId,
    getCurrentAccelerator,
    getCurrentInternetEnabled,
    getThemeName,
    getEditorErrorMessage,
    getJupyterProxyUrl,
  ],
  (
    sourceType,
    status,
    kernelRunId,
    accelerator,
    isInternet,
    themeName,
    errorMessage,
    jupyterProxyUrl
  ): KernelTelemetryData => ({
    sourceType,
    status,
    kernelRunId,
    accelerator,
    isInternet,
    themeName,
    errorMessage,
    jupyterProxyUrl,
    jupyterLab: true,
  })
);
